import React, { useState } from 'react';
import {
  Download,
  FileText,
  Image,
  Table,
  Code,
  Loader2,
  CheckCircle,
  XCircle
} from 'lucide-react';
import Button from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const ReportExporter = ({ report, onExport }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedSections, setSelectedSections] = useState({
    summary: true,
    accuracy: true,
    latency: true,
    security: true,
    content: true,
    reliability: true,
    recommendations: true,
    rawData: false
  });

  const exportFormats = [
    {
      id: 'pdf',
      name: 'PDF Report',
      description: 'Professional formatted report with charts',
      icon: FileText,
      color: 'text-red-500'
    },
    {
      id: 'csv',
      name: 'CSV Data',
      description: 'Raw metrics data for analysis',
      icon: Table,
      color: 'text-green-500'
    },
    {
      id: 'json',
      name: 'JSON Export',
      description: 'Complete data structure',
      icon: Code,
      color: 'text-blue-500'
    },
    {
      id: 'png',
      name: 'Chart Images',
      description: 'High-resolution chart exports',
      icon: Image,
      color: 'text-purple-500'
    }
  ];

  const sectionOptions = [
    { id: 'summary', name: 'Executive Summary', description: 'High-level overview and key metrics' },
    { id: 'accuracy', name: 'Accuracy Analysis', description: 'Detailed accuracy metrics and distribution' },
    { id: 'latency', name: 'Latency Analysis', description: 'Response time metrics and performance' },
    { id: 'security', name: 'Security Analysis', description: 'Security assessment and vulnerabilities' },
    { id: 'content', name: 'Content Safety', description: 'Offensive content detection results' },
    { id: 'reliability', name: 'Reliability Analysis', description: 'Hallucination detection and factual accuracy' },
    { id: 'recommendations', name: 'Recommendations', description: 'Actionable improvement suggestions' },
    { id: 'rawData', name: 'Raw Data', description: 'Complete dataset and detailed results' }
  ];

  const handleExport = async () => {
    if (!report) return;

    setIsExporting(true);
    setExportStatus(null);

    try {
      // Prepare export configuration
      const exportConfig = {
        format: selectedFormat,
        sections: selectedSections,
        include_charts: selectedFormat === 'pdf' || selectedFormat === 'png',
        include_raw_data: selectedSections.rawData,
      };

      // Call the export function passed from parent
      if (onExport) {
        await onExport(exportConfig);
      } else {
        // Default export implementation
        await performExport(exportConfig);
      }

      setExportStatus('success');
    } catch (error) {
      console.error('Export failed:', error);
      setExportStatus('error');
    } finally {
      setIsExporting(false);
    }
  };

  const performExport = async (exportConfig) => {
    // Mock export implementation - replace with actual API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Generate mock download based on format
    const mockData = {
      pdf: () => generateTextReport(report),
      csv: () => generateCSV(report),
      json: () => JSON.stringify(report, null, 2),
      png: () => 'Mock image data'
    };

    const content = mockData[exportConfig.format]();
    const blob = new Blob([content], { 
      type: exportConfig.format === 'json' ? 'application/json' : 'text/plain' 
    });
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `report-${report.id}.${exportConfig.format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };



  const generateCSV = (reportData) => {
    const rows = [];

    // Header
    rows.push(['Metric', 'Value', 'Description']);

    // Overall score
    rows.push(['Overall Score', `${reportData.overall_score}%`, 'Overall evaluation performance']);

    // Accuracy metrics
    if (reportData.metrics.accuracy) {
      const acc = reportData.metrics.accuracy;
      rows.push(['Accuracy Score', `${acc.score}%`, 'Overall accuracy percentage']);
      rows.push(['Total Responses', acc.total_responses, 'Total number of responses']);
      rows.push(['Accurate Responses', acc.accurate_responses, 'Number of accurate responses']);
      rows.push(['High Quality Rate', `${acc.high_quality_rate}%`, 'Percentage of high quality responses']);
    }

    // Latency metrics
    if (reportData.metrics.latency) {
      const lat = reportData.metrics.latency;
      rows.push(['Average Latency', `${lat.average_ms}ms`, 'Average response time']);
      rows.push(['Median Latency', `${lat.median_ms}ms`, 'Median response time']);
      rows.push(['95th Percentile', `${lat.p95_ms}ms`, '95th percentile response time']);
      rows.push(['Performance Grade', lat.performance_grade, 'Overall performance grade']);
    }

    // Security metrics
    if (reportData.metrics.security) {
      const sec = reportData.metrics.security;
      rows.push(['Security Score', `${sec.score}%`, 'Overall security score']);
      rows.push(['Risk Level', sec.risk_level, 'Security risk assessment']);
      rows.push(['Vulnerabilities Found', sec.vulnerabilities_found, 'Number of security issues']);
    }

    return rows.map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
  };

  const generateTextReport = (reportData) => {
    let content = `EVALUATION REPORT\n`;
    content += `=================\n\n`;
    content += `Report ID: ${reportData.id}\n`;
    content += `Generated: ${new Date(reportData.generated_at).toLocaleString()}\n`;
    content += `Overall Score: ${reportData.overall_score}%\n\n`;

    if (reportData.summary) {
      content += `EXECUTIVE SUMMARY\n`;
      content += `-----------------\n`;
      content += `${reportData.summary}\n\n`;
    }

    if (reportData.metrics.accuracy) {
      content += `ACCURACY METRICS\n`;
      content += `----------------\n`;
      content += `Score: ${reportData.metrics.accuracy.score}%\n`;
      content += `Total Responses: ${reportData.metrics.accuracy.total_responses}\n`;
      content += `Accurate Responses: ${reportData.metrics.accuracy.accurate_responses}\n`;
      content += `High Quality Rate: ${reportData.metrics.accuracy.high_quality_rate}%\n\n`;
    }

    if (reportData.metrics.latency) {
      content += `LATENCY METRICS\n`;
      content += `---------------\n`;
      content += `Average: ${reportData.metrics.latency.average_ms}ms\n`;
      content += `Median: ${reportData.metrics.latency.median_ms}ms\n`;
      content += `95th Percentile: ${reportData.metrics.latency.p95_ms}ms\n`;
      content += `Performance Grade: ${reportData.metrics.latency.performance_grade}\n\n`;
    }

    if (reportData.recommendations) {
      content += `RECOMMENDATIONS\n`;
      content += `---------------\n`;
      Object.entries(reportData.recommendations).forEach(([category, items]) => {
        content += `${category.toUpperCase()}:\n`;
        items.forEach(item => {
          content += `  - ${item}\n`;
        });
        content += `\n`;
      });
    }

    return content;
  };

  const toggleSection = (sectionId) => {
    setSelectedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const selectAllSections = () => {
    const allSelected = Object.values(selectedSections).every(Boolean);
    const newState = {};
    Object.keys(selectedSections).forEach(key => {
      newState[key] = !allSelected;
    });
    setSelectedSections(newState);
  };

  if (!report) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No report selected for export</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Download className="w-5 h-5" />
            <span>Export Report</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Export Format Selection */}
          <div>
            <h4 className="text-lg font-medium mb-3">Export Format</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {exportFormats.map((format) => {
                const Icon = format.icon;
                return (
                  <div
                    key={format.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedFormat === format.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedFormat(format.id)}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className={`w-6 h-6 ${format.color}`} />
                      <div>
                        <p className="font-medium">{format.name}</p>
                        <p className="text-xs text-muted-foreground">{format.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-medium">Include Sections</h4>
              <Button variant="outline" size="sm" onClick={selectAllSections}>
                {Object.values(selectedSections).every(Boolean) ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sectionOptions.map((section) => (
                <div
                  key={section.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedSections[section.id]
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-4 h-4 rounded border-2 mt-0.5 ${
                      selectedSections[section.id]
                        ? 'bg-primary border-primary'
                        : 'border-border'
                    }`}>
                      {selectedSections[section.id] && (
                        <CheckCircle className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{section.name}</p>
                      <p className="text-xs text-muted-foreground">{section.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Export Status */}
          {exportStatus && (
            <div className={`p-4 rounded-lg flex items-center space-x-3 ${
              exportStatus === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            }`}>
              {exportStatus === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <XCircle className="w-5 h-5" />
              )}
              <p>
                {exportStatus === 'success'
                  ? 'Report exported successfully!'
                  : 'Export failed. Please try again.'
                }
              </p>
            </div>
          )}

          {/* Export Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleExport}
              disabled={isExporting || !Object.values(selectedSections).some(Boolean)}
              className="min-w-[120px]"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export Report
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportExporter;
